import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { useWorkspace } from '../common/WorkspaceProvider';

function ScrollTopControl({ obscured }: { obscured: boolean }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const scrollRoot = useRef<HTMLElement | null>(null);
  const pastThreshold = useRef(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;
    // 공개 화면은 문서 스크롤이며 overflow 부모가 있으면 같은 대상을 읽고 이동한다.
    let parent = button.parentElement;
    while (parent && parent !== document.body && parent !== document.documentElement) {
      if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) break;
      parent = parent.parentElement;
    }
    const root = parent && parent !== document.body && parent !== document.documentElement
      ? parent : (document.scrollingElement || document.documentElement) as HTMLElement;
    scrollRoot.current = root;
    const target = root === document.scrollingElement || root === document.documentElement ? document : root;
    const update = () => {
      const next = root.scrollTop >= 600;
      if (next !== pastThreshold.current) {
        pastThreshold.current = next;
        setVisible(next);
      }
    };
    const navigation = button.closest('.promotion-document')?.querySelector('.promotion-navigation');
    const position = () => {
      const height = navigation?.getBoundingClientRect().height || 0;
      if (height) button.style.bottom = `${height + 24}px`;
      else button.style.removeProperty('bottom');
    };
    const observer = navigation ? new ResizeObserver(position) : null;
    if (navigation) observer?.observe(navigation);
    position();
    update();
    target.addEventListener('scroll', update, { passive: true });
    return () => {
      target.removeEventListener('scroll', update);
      observer?.disconnect();
      scrollRoot.current = null;
    };
  }, []);

  const shown = visible && !obscured;
  return <button ref={buttonRef} type="button" aria-label="맨 위로 이동"
    aria-hidden={!shown} disabled={!shown} tabIndex={shown ? 0 : -1}
    className={`promotion-scroll-top${shown ? ' promotion-scroll-top--visible' : ''}`}
    onClick={() => {
      const root = scrollRoot.current;
      if (!root) return;
      const behavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      root.scrollTop = 0;
      root.style.scrollBehavior = behavior;
      pastThreshold.current = false;
      setVisible(false);
    }}><ArrowUp size={20} aria-hidden="true" /></button>;
}

export default function PromotionScrollTop({ obscured }: { obscured: boolean }) {
  const workspace = useWorkspace();
  return workspace.mode === 'public' ? <ScrollTopControl obscured={obscured} /> : null;
}
